import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';

interface Offset {
  x: number;
  y: number;
}

interface DraggablePanel {
  offset: Offset;
  dragging: boolean;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
}

const VIEWPORT_MARGIN = 8;

export const useDraggablePanel = (
  panelRef: RefObject<HTMLElement>,
  handleSelector: string,
  enabled: boolean
): DraggablePanel => {
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      const panel = panelRef.current;
      if (
        !enabled ||
        !panel ||
        event.button !== 0 ||
        !target.closest(handleSelector) ||
        target.closest('button, a, input, textarea, [role="button"]')
      ) {
        return;
      }

      event.preventDefault();
      const start = { x: event.clientX, y: event.clientY };
      const startOffset = offsetRef.current;
      const rect = panel.getBoundingClientRect();
      // How far the panel may move in each direction before leaving the viewport
      const bounds = {
        minX: startOffset.x - (rect.left - VIEWPORT_MARGIN),
        maxX:
          startOffset.x + (window.innerWidth - VIEWPORT_MARGIN - rect.right),
        minY: startOffset.y - (rect.top - VIEWPORT_MARGIN),
        maxY:
          startOffset.y + (window.innerHeight - VIEWPORT_MARGIN - rect.bottom),
      };
      const clamp = (value: number, min: number, max: number): number =>
        Math.min(Math.max(value, min), max);

      const handleMove = (moveEvent: PointerEvent): void => {
        setOffset({
          x: clamp(
            startOffset.x + moveEvent.clientX - start.x,
            bounds.minX,
            bounds.maxX
          ),
          y: clamp(
            startOffset.y + moveEvent.clientY - start.y,
            bounds.minY,
            bounds.maxY
          ),
        });
      };
      const handleUp = (): void => {
        setDragging(false);
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);
      };

      setDragging(true);
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [enabled, handleSelector, panelRef]
  );

  return { offset: enabled ? offset : { x: 0, y: 0 }, dragging, onPointerDown };
};
